import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IDropdown } from '@models/dropdown.model';
import { selectAddOrganizationFilteredTypes } from '@store/selectors/add-organization/add-organization.selectors';
import { AddOrganizationActions } from '@store/actions/add-organization/add-organization.actions';
import { UserService } from '@services/user.service';
import { ROLE_GROUPS } from '@constants/roles';

@Component({
  selector: 'app-edit-add-organization-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
  ],
  templateUrl: './edit-add-organization-modal.component.html',
  styleUrl: './edit-add-organization-modal.component.css'
})
export class EditAddOrganizationModalComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<EditAddOrganizationModalComponent>);
  private readonly userService = inject(UserService);
  private readonly destroy$ = new Subject<void>();

  organizationForm!: FormGroup;
  isLoading = false;
  defaultRegistry = {
    id: 1000,
    name: "REG-ENT"
  };

  organizationTypes$: Observable<IDropdown[]> = this.store.select(selectAddOrganizationFilteredTypes);
  isSuperAdmin$: Observable<boolean> = this.userService.hasRequiredRoles(ROLE_GROUPS.SUPER_USER);

  ngOnInit(): void {
    this.organizationForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(256)]],
      organizationTypeId: ['', [Validators.required]],
      isTestOrganization: [false],
    });

    this.actions$.pipe(
      ofType(AddOrganizationActions.createNewOrganizationSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isLoading = false;
      this.dialogRef.close();
    });

    this.actions$.pipe(
      ofType(AddOrganizationActions.createNewOrganizationFailure),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.isLoading = false;
    });

    this.store.dispatch(AddOrganizationActions.pageLoaded());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSave() {
    if (this.organizationForm.invalid || this.isLoading) {
      return;
    }
    
    this.isLoading = true;
    
    const formData = {
      ...this.organizationForm.value,
      registryId: this.defaultRegistry.id,
      isTestOrganization: this.organizationForm.value.isTestOrganization ? 1 : 0,
    };
    
    this.store.dispatch(AddOrganizationActions.createNewOrganization({
      organizationData: formData
    }));
  }

  onCancel(): void {
    this.dialogRef.close();
  }


  private markFormGroupTouched(): void {
    Object.keys(this.organizationForm.controls).forEach(key => {
      const control = this.organizationForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  getErrorMessage(controlName: string): string {
    const control = this.organizationForm.get(controlName);
    if (control?.hasError('required')) {
      return `${this.getDisplayName(controlName)} is required`;
    }
    if (control?.hasError('maxlength')) {
      const maxLength = control.errors?.['maxlength']?.requiredLength;
      return `${this.getDisplayName(controlName)} cannot exceed ${maxLength} characters`;
    }
    if (control?.hasError('pattern')) {
      if (controlName === 'zipCode') {
        return 'Please enter a valid zip code (12345 or 12345-6789)';
      }
    }
    return '';
  }

  private getDisplayName(controlName: string): string {
    const displayNames: { [key: string]: string } = {
      name: 'Organization Name',
      orgTypeId: 'Type of Center',
      id: 'Organization ID',
      legacyId: 'Legacy ID',
      crmId: 'CRM ID',
      address: 'Address',
      buildingSuiteFloor: 'Building/Suite/Floor',
      zipCode: 'Zip Code',
      city: 'City',
      state: 'State',
    };
    return displayNames[controlName] || controlName;
  }



}